import type { LiubaLocalLessonVenueId } from "@/lib/localLessons/liubaVenues";

export const LOCAL_LESSON_MANUAL_ORDERING_COPY =
  "Secure your lesson online. After payment, we will confirm your exact lesson time with you. Your appointment is final only when you receive the separate lesson confirmation." as const;

export const BURY_MANUAL_ORDERING_COPY =
  "We will verify your lesson time with The Rehearsal Rooms before confirming the appointment. If the requested time is unavailable, we will offer alternative dates or arrange a full refund." as const;

export const LOCAL_LESSON_AUTOMATIC_ORDERING_COPY =
  "Choose a provisional time from Liuba's live availability, then complete secure payment. Payment and appointment acceptance remain separate, and your appointment is final only after the separate lesson confirmation." as const;

export type LocalLessonOrderingMode = "automatic" | "manual" | "unavailable";

type Env = Record<string, string | undefined>;

const DEPLOYED_ORDERING_ENV: Env = {
  LOCAL_LESSONS_MANUAL_ORDERING_ENABLED:
    process.env.LOCAL_LESSONS_MANUAL_ORDERING_ENABLED,
  ELY_LOCAL_LESSONS_ENABLED: process.env.ELY_LOCAL_LESSONS_ENABLED,
  BURY_LOCAL_LESSONS_ENABLED: process.env.BURY_LOCAL_LESSONS_ENABLED,
};

function parseBooleanFlag(value: string | undefined) {
  if (value === undefined || value.trim() === "") return { value: false, valid: true };
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return { value: true, valid: true };
  if (normalized === "false") return { value: false, valid: true };
  return { value: false, valid: false };
}

export function getLocalLessonOrderingConfig(env: Env = DEPLOYED_ORDERING_ENV) {
  const manual = parseBooleanFlag(env.LOCAL_LESSONS_MANUAL_ORDERING_ENABLED);
  const elyAutomatic = parseBooleanFlag(env.ELY_LOCAL_LESSONS_ENABLED);
  const buryAutomatic = parseBooleanFlag(env.BURY_LOCAL_LESSONS_ENABLED);
  const configurationValid = manual.valid && elyAutomatic.valid && buryAutomatic.valid;
  const manualOrderingEnabled = configurationValid && manual.value;
  const elyAutomaticEnabled = configurationValid && manual.value && elyAutomatic.value;
  const buryAutomaticEnabled = configurationValid && manual.value && buryAutomatic.value;
  const elyOrderingEnabled = manualOrderingEnabled || elyAutomaticEnabled;
  const buryOrderingEnabled = manualOrderingEnabled || buryAutomaticEnabled;

  return {
    configurationValid,
    orderingAvailable: elyOrderingEnabled || buryOrderingEnabled,
    manualOrderingEnabled,
    elyOrderingEnabled,
    buryOrderingEnabled,
    elyAutomaticEnabled,
    buryAutomaticEnabled,
  } as const;
}

export function assertManualLocalLessonOrderingEnabled(env: Env = DEPLOYED_ORDERING_ENV) {
  const config = getLocalLessonOrderingConfig(env);
  if (!config.manualOrderingEnabled) {
    throw new Error("local_lessons_manual_ordering_disabled");
  }
  return config;
}

export function manualOrderingCopyForVenue(venueId: LiubaLocalLessonVenueId) {
  return venueId === "bury_st_edmunds_studio"
    ? `${LOCAL_LESSON_MANUAL_ORDERING_COPY} ${BURY_MANUAL_ORDERING_COPY}`
    : LOCAL_LESSON_MANUAL_ORDERING_COPY;
}

export function localLessonOrderingCopyForVenue(
  venueId: LiubaLocalLessonVenueId,
  mode: LocalLessonOrderingMode,
) {
  if (mode === "automatic") {
    return venueId === "bury_st_edmunds_studio"
      ? `${LOCAL_LESSON_AUTOMATIC_ORDERING_COPY} We verify The Rehearsal Rooms before final confirmation.`
      : LOCAL_LESSON_AUTOMATIC_ORDERING_COPY;
  }
  if (mode === "manual") return manualOrderingCopyForVenue(venueId);
  return "Online booking is currently closed for this studio. Contact the team for current availability.";
}
